import type { ReactNode } from 'react';
import { Children, isValidElement, useId } from 'react';

import classNames from 'classnames';

import styles from './Slider.module.css';

export interface SliderProps {
  /**
   * Container height matches the highest slide by default. Set to `false` to
   * match the height of the `currentSlide`
   *
   * @defaultValue true
   */
  fixedHeight?: boolean;
  /**
   * How many slides are displayed at a time
   *
   * @defaultValue `1`
   */
  visibleSlides?: number;
  /**
   * Toggle horizontal scroll animation
   *
   * @defaultValue `true`
   */
  animate?: boolean;
  /**
   * Set the index or the name of the slide that's currently visible.
   *
   * @defaultValue `0`
   */
  currentSlide?: number | string;
  /** Each child HTML or react element will render a slide inside Slider */
  children?: ReactNode | Array<ReactNode>;
  /** Optional className */
  className?: string;
  /** Pass a custom ID. By default, will use an auto-generated id. */
  id?: string;
}

/**
 * Render a set of slides that scrolls horizontally. Touch gestures are not
 * natively supported by `Slider` and the `currentSlide` is controlled by
 * external stateful logic.
 *
 * @see https://corgi-x.tfd.engineering/components/slider
 */
export function Slider({
  currentSlide = 0,
  fixedHeight = true,
  visibleSlides = 1,
  animate = true,
  className,
  id,
  ...props
}: SliderProps) {
  const reactId = useId();
  const sliderId = id || reactId;

  const slides = Children.toArray(props.children);
  let slideIndex: number;
  if (typeof currentSlide === 'string') {
    slideIndex = slides.findIndex(slide => {
      if (!isValidElement(slide)) {
        return false;
      }
      if (!('data-slide-name' in slide.props)) {
        return false;
      }
      return slide.props['data-slide-name'] === currentSlide;
    });
  } else {
    slideIndex = currentSlide;
  }

  const slidesCount = slides.length;
  const slideWidth = 100 / visibleSlides / slidesCount;
  const dynamicSliderStyles = {
    width: `${(100 / visibleSlides) * slidesCount}%`,
    transform: `translateX(${-slideIndex * slideWidth * visibleSlides}%)`,
  };
  const animateSlides = animate ? styles.animate : '';

  return (
    <div
      className={classNames(
        styles['slides-container'],
        animateSlides,
        className
      )}
      id={sliderId}
      {...props}
    >
      <div className={styles.slides} style={dynamicSliderStyles}>
        {slides.map((child: ReactNode, index: number) => {
          const visible =
            index >= slideIndex && index < slideIndex + visibleSlides;

          const visibility = !visible ? 'hidden' : 'visible';
          const maxHeight = {
            maxHeight: !fixedHeight && !visible ? 1 : 'none',
          };

          if (!isValidElement(child)) {
            return <></>;
          }
          const slideName =
            'data-slide-name' in child.props
              ? (child.props['data-slide-name'] as string)
              : undefined;
          return (
            <div
              key={index}
              className={classNames(styles.slide, styles[visibility])}
              data-slide-name={slideName}
              style={maxHeight}
            >
              {child}
            </div>
          );
        })}
      </div>
    </div>
  );
}
